import {
  getNestedPathValue,
  isPlainObject,
  setNestedPathValue,
} from "./common";

const mergeNested = (base, override) => {
  if (!isPlainObject(override)) {
    return override;
  }

  return Object.entries(override).reduce(
    (acc, [key, value]) => ({
      ...acc,
      [key]:
        isPlainObject(value) && isPlainObject(acc[key])
          ? mergeNested(acc[key], value)
          : value,
    }),
    { ...(isPlainObject(base) ? base : {}) },
  );
};

const getOperatorFieldNames = inputs =>
  inputs.reduce((acc, input) => {
    if (input?.name && input?.hasOperator) {
      return [...acc, input.name];
    }

    if (Array.isArray(input?.inputs)) {
      return [...acc, ...getOperatorFieldNames(input.inputs)];
    }

    return acc;
  }, []);

const mapParsedFilterToFormikValues = ({ parsedFilter, operatorFieldNames }) => {
  const toFormik = (value, path = "") => {
    if (!isPlainObject(value)) {
      return value;
    }

    if (typeof value.operator === "string") {
      // `isNull` is a value-less operator: stripping it (the default for
      // non-operator fields) would leave `undefined` in formik, so the URL
      // value would be lost on reload. Preserve the operator object instead
      // so toggle/checkbox UIs that derive their checked state from it
      // (e.g. the "Pending" filter on Contact Search > Requests, which
      // binds to `_request.dateClosed.operator === "isNull"`) survive the
      // URL roundtrip.
      if (value.operator === "isNull") {
        return value;
      }
      return operatorFieldNames.has(path) ? value : value.value;
    }

    return Object.entries(value).reduce(
      (acc, [key, nestedValue]) => ({
        ...acc,
        [key]: toFormik(nestedValue, path ? `${path}.${key}` : key),
      }),
      {},
    );
  };

  return toFormik(parsedFilter);
};

const getMultiSelectFieldConfigs = inputs =>
  inputs.reduce((acc, input) => {
    if (Array.isArray(input?.inputs)) {
      return [...acc, ...getMultiSelectFieldConfigs(input.inputs)];
    }

    if (input?.type === "multi-select" && input?.name) {
      return [
        ...acc,
        {
          hasOperator: Boolean(input?.hasOperator),
          isMulti: input?.isMulti !== false,
          name: input.name,
          options: input?.options || [],
        },
      ];
    }

    return acc;
  }, []);

const toSelectOption = (value, options = []) => {
  if (
    isPlainObject(value) &&
    Object.prototype.hasOwnProperty.call(value, "value") &&
    Object.prototype.hasOwnProperty.call(value, "label")
  ) {
    return value;
  }

  const matchedOption = options.find(
    option => String(option?.value) === String(value),
  );

  if (matchedOption) {
    return matchedOption;
  }

  return { label: String(value), value };
};

const mapHydratedMultiSelectValue = ({ currentValue, options, isMulti }) => {
  if (Array.isArray(currentValue)) {
    return currentValue.map(value => toSelectOption(value, options));
  }

  if (typeof currentValue === "undefined" || currentValue === null) {
    return currentValue;
  }

  return isMulti
    ? [toSelectOption(currentValue, options)]
    : toSelectOption(currentValue, options);
};

const hydrateMultiSelectFormikValues = ({ hydratedValues, multiSelectConfigs }) => {
  return multiSelectConfigs.reduce((acc, config) => {
    const currentFieldValue = getNestedPathValue(acc, config.name);

    if (typeof currentFieldValue === "undefined" || currentFieldValue === null) {
      return acc;
    }

    if (config.hasOperator && isPlainObject(currentFieldValue)) {
      if (currentFieldValue.operator === "isNull") {
        return acc;
      }

      const mappedValue = mapHydratedMultiSelectValue({
        currentValue: currentFieldValue.value,
        options: config.options,
        isMulti: config.isMulti,
      });

      return setNestedPathValue(acc, config.name, {
        ...currentFieldValue,
        value: mappedValue,
      });
    }

    const mappedValue = mapHydratedMultiSelectValue({
      currentValue: currentFieldValue,
      options: config.options,
      isMulti: config.isMulti,
    });

    return setNestedPathValue(acc, config.name, mappedValue);
  }, { ...hydratedValues });
};

export {
  getMultiSelectFieldConfigs,
  getOperatorFieldNames,
  hydrateMultiSelectFormikValues,
  mapParsedFilterToFormikValues,
  mergeNested,
};
